import {
  EXPANDABLE_DETAILS_KEY,
  FIELD_ORDER_UI_SCHEMA_KEY,
  NUM_COLUMNS_KEY,
  SECTION_NAME_UI_SCHEMA_KEY,
  ShapeUISchemaNestedShapeValues,
  SHOW_DIVIDER_LINE_KEY,
  StepConfigEditorOverride,
} from '../../StepConfigEditorTypes';
import {isXAxisColumnNameNotNumeric} from '../StepConfigDisableFieldFunctions';
import {
  createDiscreteColorPaletteSection,
  createLegendStyleSection,
  createMarkerStyleSection,
} from './ChartSchemaHelpers';

const BOX_PLOT_X_AXIS_CONFIG_GROUP = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: [
    'xAxisColumnSection',
    'xSortDirectionSection',
    'xAxisRange',
    'xAxisLabelSection',
    'pointLabelSection',
  ],
  [SECTION_NAME_UI_SCHEMA_KEY]: 'X-axis',
  xAxisColumnSection: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['xAxisColumnName'],
    xAxisColumnName: {
      displayName: 'Column',
      configShapePath: ['chartConfig', 'parameters', 'xAxisColumnName'],
    },
  },
  xSortDirectionSection: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['xSortDirection'],
    xSortDirection: {
      displayName: 'Sort',
      configShapePath: ['chartConfig', 'parameters', 'sortDirection'],
      fieldSettings: {
        displayNames: {
          ASCENDING: 'Ascending',
          DESCENDING: 'Descending',
        },
      },
    },
  },
  xAxisRange: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['xAxisMin', 'xAxisMax'],
    [NUM_COLUMNS_KEY]: 2,
    xAxisMin: {
      displayName: 'Range min',
      configShapePath: ['chartConfig', 'parameters', 'xAxisMin'],
      disableFn: isXAxisColumnNameNotNumeric,
    },
    xAxisMax: {
      displayName: 'Range max',
      configShapePath: ['chartConfig', 'parameters', 'xAxisMax'],
      disableFn: isXAxisColumnNameNotNumeric,
    },
  },
  xAxisLabelSection: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['xAxisLabel'],
    xAxisLabel: {
      displayName: 'Custom axis label',
      configShapePath: ['chartConfig', 'parameters', 'xAxisLabel'],
    },
  },
  pointLabelSection: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['pointLabelColumnName'],
    pointLabelColumnName: {
      displayName: 'Label points by',
      configShapePath: ['chartConfig', 'parameters', 'pointLabelColumnName'],
      infoText: 'Data from this column will be shown when hovering over a point in the chart.',
      fieldSettings: {
        noColumnSelectedText: 'None',
        allowSelectNull: true,
      },
    },
  },
} as const satisfies ShapeUISchemaNestedShapeValues;

const BOX_PLOT_Y_AXIS_CONFIG_GROUP = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: [
    'yAxisColumnSection',
    'yAxisRange',
    'invertYAxisSection',
    'yAxisLabelSection',
  ],
  [SECTION_NAME_UI_SCHEMA_KEY]: 'Y-axis',
  yAxisColumnSection: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['yAxisColumnName'],
    yAxisColumnName: {
      displayName: 'Column',
      configShapePath: ['chartConfig', 'parameters', 'yAxisColumnName'],
    },
  },
  yAxisRange: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['yAxisMin', 'yAxisMax'],
    [NUM_COLUMNS_KEY]: 2,
    yAxisMin: {
      displayName: 'Range min',
      configShapePath: ['chartConfig', 'parameters', 'yAxisMin'],
    },
    yAxisMax: {
      displayName: 'Range max',
      configShapePath: ['chartConfig', 'parameters', 'yAxisMax'],
    },
  },
  invertYAxisSection: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['invertYAxis'],
    invertYAxis: {
      configShapePath: ['chartConfig', 'parameters', 'invertYAxis'],
      editorOverride: StepConfigEditorOverride.CHECKBOX,
      fieldSettings: {
        checkboxLabel: 'Invert Y-axis',
      },
    },
  },
  yAxisLabelSection: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['yAxisLabel'],
    yAxisLabel: {
      displayName: 'Custom axis label',
      configShapePath: ['chartConfig', 'parameters', 'yAxisLabel'],
    },
  },
} as const satisfies ShapeUISchemaNestedShapeValues;

/**
 * Box Plot configuration sections for the Configure tab
 */
export const BOX_PLOT_CONFIG_SECTIONS: ShapeUISchemaNestedShapeValues = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: [
    'nameSection',
    'boxPointsSection',
    'xAxisSection',
    'yAxisSection',
    'seriesAndColorPaletteSection',
  ],
  nameSection: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['chartName'],
    [SHOW_DIVIDER_LINE_KEY]: true,
    chartName: {
      displayName: 'Title',
      configShapePath: ['chartName'],
    },
  },
  boxPointsSection: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['boxPoints'],
    [SHOW_DIVIDER_LINE_KEY]: true,
    boxPoints: {
      displayName: 'Outlier & point display',
      configShapePath: ['chartConfig', 'parameters', 'showPoints'],
      fieldSettings: {
        displayNames: {
          ALL: 'All points',
          OUTLIERS: 'Outliers only',
          HIDE: 'Hide points',
        },
      },
    },
  },
  xAxisSection: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['xAxisGroup'],
    xAxisGroup: BOX_PLOT_X_AXIS_CONFIG_GROUP,
  },
  yAxisSection: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['yAxisGroup'],
    yAxisGroup: BOX_PLOT_Y_AXIS_CONFIG_GROUP,
  },
  seriesAndColorPaletteSection: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['seriesSection', 'colorSection'],
    [SHOW_DIVIDER_LINE_KEY]: true,
    [SECTION_NAME_UI_SCHEMA_KEY]: 'Series & Color Palette',
    seriesSection: {
      [FIELD_ORDER_UI_SCHEMA_KEY]: ['seriesColumnName'],
      seriesColumnName: {
        displayName: 'Series',
        configShapePath: ['chartConfig', 'parameters', 'seriesColumnName'],
        fieldSettings: {
          noColumnSelectedText: 'No series',
          allowSelectNull: true,
        },
      },
    },
    colorSection: createDiscreteColorPaletteSection(['chartConfig', 'parameters', 'seriesColumnName']),
  },
} as const;

/**
 * Box Plot style sections for the Style tab
 */
export const BOX_PLOT_STYLE_SECTIONS = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['legendSection', 'pointStyleSection', 'boxStyleSection'],
  legendSection: createLegendStyleSection(),
  pointStyleSection: createMarkerStyleSection({
    sectionName: 'Point styling',
    shapeFieldName: 'Point style',
  }),
  boxStyleSection: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['fillOpacity', 'boxLineStyleSection'],
    [SHOW_DIVIDER_LINE_KEY]: true,
    [SECTION_NAME_UI_SCHEMA_KEY]: 'Box style',
    fillOpacity: {
      [FIELD_ORDER_UI_SCHEMA_KEY]: ['fillOpacity'],
      fillOpacity: {
        displayName: 'Fill opacity',
        configShapePath: ['chartConfig', 'parameters', 'fillOpacity'],
      },
    },
    boxLineStyleSection: {
      [FIELD_ORDER_UI_SCHEMA_KEY]: ['lineWidth', 'lineWidthOverride'],
      [EXPANDABLE_DETAILS_KEY]: {
        headerField: 'lineWidth',
        detailFields: ['lineWidthOverride'],
      },

      lineWidth: {
        displayName: 'Line width',
        configShapePath: ['chartConfig', 'parameters', 'lineWidth'],
        fieldSettings: {
          displayMode: 'icon',
          displayNames: {
            THIN: 'Thin',
            THICK: 'Thick',
            NONE: 'None',
          },
          iconNames: {
            THIN: 'minus',
            THICK: 'rectangle-wide',
            NONE: 'ban',
          },
        },
        editorOverride: StepConfigEditorOverride.ICON_SELECTOR,
      },
      lineWidthOverride: {
        displayName: 'Line weight',
        configShapePath: ['chartConfig', 'parameters', 'lineWidthOverride'],
      },
    },
  },
} as const satisfies ShapeUISchemaNestedShapeValues;
